import { ConversationActions } from '@/components/admin/sessions/conversation/conversation-actions';
import { ConversationScreen } from '@/components/admin/sessions/conversation/conversation-screen';
import { PmsSpinner } from '@/components/ui/pms-spinner';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { ApiError, fetchModelParams, modelSettingsApi, ruleSettingsApi } from '@/lib/api';
import { readArrivalTurn, readOpenLayers, writeOpenLayers } from '@/lib/conversation-model';
import { fetchGuardrails, fetchRules } from '@/lib/rules-api';
import { SESSION_DETAIL_KEY, fetchSessionDetail } from '@/lib/session-api';
import { MODEL_SETTINGS_ID, RULE_SETTINGS_ID } from '@matter_extractor/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';

/**
 * `/admin/sessions/<id>/protocole` - la conversation du protocole (§12.3,
 * maquette `docs/specs/maquettes/sessions/conversation.html`, issue #71).
 *
 * ## Le `_` de `$sessionId_`, et pourquoi il porte l'écran
 *
 * §12.3 : « sur sa propre page, frère du parcours et pas son enfant : elle prend
 * l'écran seule ». TanStack's flat file routing nests `a.$b.c.tsx` UNDER
 * `a.$b.tsx`, which would render the conversation inside the parcours; the
 * trailing `_` is the router's own opt-out, and the path is unchanged. C'est le
 * même geste que `experiences.$experienceId_.pages.$pageId.tsx`, et pour la même
 * raison.
 *
 * ## Quatre lectures, et une seule qui se relit
 *
 * La session porte sa propre clé, partagée avec le parcours : « les deux écrans
 * sont deux vues d'une lecture », donc une conversation ouverte depuis un
 * parcours ne redemande pas ce que le parcours tient déjà. Les trois autres sont
 * des RÉGLAGES - garde-fous, règles, budget - que ni §8.5 ni §11.2 ne figent, et
 * qui sont le seul endroit d'où un garde-fou tire son nom et son N. Aucune des
 * quatre ne bloque l'écran : un garde-fou que Réglages ne résout plus s'affiche
 * par son identifiant, et le budget absent retire la jauge sans retirer le coût.
 *
 * ## Une session en cours se relit, et elle seule
 *
 * §12.3 : « la relecture s'arrête au premier statut final », et aucun canal
 * nouveau - le SSE de §9.5 est celui de la personne. D'où un `refetchInterval`
 * qui lit le statut de la réponse précédente et rend `false` dès qu'il n'est
 * plus `in-progress`.
 *
 * ## Ce qui voyage dans l'URL, et ce qui n'y voyage pas
 *
 * `?couches=` porte les six bascules, `?tour=` le tour d'ARRIVÉE (issue #172) :
 * l'écran y défile, l'éclaire et y amorce la colonne État, puis le regard du
 * lecteur la déplace sans que rien ne revienne dans l'URL - le survol n'y écrit
 * rien, et « Tour N » COPIE le lien au lieu de l'y écrire. Les dépliages à la
 * main, eux, restent dans la page (§12.3). `validateSearch` ne jette JAMAIS : un
 * paramètre abîmé retombe sur l'état d'arrivée plutôt que sur un écran blanc.
 */

type ConversationSearch = {
  couches?: string | undefined;
  tour?: string | undefined;
};

/**
 * Les deux replis des réglages, en CONSTANTES.
 *
 * `?? []` écrit à l'appel rend un tableau neuf à chaque rendu, ce qui invalide
 * le `useMemo` qui construit les trois index de l'écran - ceux que chaque couche
 * de chaque tour parcourt. Une constante partagée garde la même identité tant
 * que la lecture n'a pas atterri.
 */
const NO_GUARDRAILS: readonly [] = [];
const NO_RULES: readonly [] = [];

/**
 * Les deux paramètres, lus comme le routeur les livre.
 *
 * Le `parseSearch` par défaut de TanStack Router passe chaque valeur par
 * `JSON.parse` : le `?tour=4` d'un lien tapé à la main, de l'onglet Matière ou
 * d'un outil de l'assistant (§20.5) arrive en NOMBRE, là où une valeur écrite
 * en chaîne - ce que faisait l'épingle d'avant #172 - revient en chaîne. Les
 * deux disent le même tour, et une lecture qui n'acceptait que la chaîne rendait tout lien
 * profond muet (issue #94) - le piège que `packages/frontend/CLAUDE.md` décrit
 * déjà pour `readFlag`.
 */
export function readSearch(search: Record<string, unknown>): ConversationSearch {
  const text = (value: unknown): string | undefined => {
    const raw =
      typeof value === 'string'
        ? value.trim()
        : typeof value === 'number' && Number.isFinite(value)
          ? String(value)
          : '';
    return raw === '' ? undefined : raw;
  };
  const couches = text(search.couches);
  const tour = text(search.tour);
  return {
    ...(couches === undefined ? {} : { couches }),
    ...(tour === undefined ? {} : { tour }),
  };
}

export const Route = createFileRoute('/admin/sessions/$sessionId_/protocole')({
  validateSearch: readSearch,
  component: ConversationPage,
});

function ConversationPage() {
  const { email } = useAdminSession();
  const { sessionId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();

  const session = useQuery({
    // The key the parcours reads under: one reading, two views (§12.2, §12.3).
    queryKey: SESSION_DETAIL_KEY(email, sessionId),
    queryFn: () => fetchSessionDetail(sessionId),
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.session.status === 'in-progress' ? 5_000 : false,
  });

  // The three settings reads. `staleTime` long: they are a screen's vocabulary,
  // not its subject, and re-reading them under a conversation that is polling
  // would triple the traffic of a session in progress for nothing.
  const guardrails = useQuery({
    queryKey: ['guardrails', email],
    queryFn: fetchGuardrails,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const rules = useQuery({
    queryKey: ['global-rules', email],
    queryFn: fetchRules,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const models = useQuery({
    queryKey: ['model-params', email],
    queryFn: fetchModelParams,
    staleTime: 5 * 60_000,
    retry: false,
  });

  /*
    What the session FROZE (vitrine.md section 5, issue #192): the version of the
    rules and guardrails and the version of the models it started on. A version
    is immutable, so it is read once and never stale; the three reads above are
    only what is current, for a session of before the issue that froze nothing.
  */
  const frozenRulesId = session.data?.settings?.rules?.versionId ?? null;
  const frozenModelsId = session.data?.settings?.models?.versionId ?? null;
  const frozenRules = useQuery({
    queryKey: ['rule-settings-version', email, frozenRulesId],
    queryFn: () => ruleSettingsApi.readVersion(RULE_SETTINGS_ID, frozenRulesId ?? ''),
    enabled: frozenRulesId !== null,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
  const frozenModels = useQuery({
    queryKey: ['model-settings-version', email, frozenModelsId],
    queryFn: () => modelSettingsApi.readVersion(MODEL_SETTINGS_ID, frozenModelsId ?? ''),
    enabled: frozenModelsId !== null,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

  const rejected = useRejectOn401(session.error);
  const missing = session.error instanceof ApiError && session.error.status === 404;

  if (session.isPending) {
    return (
      <div className="flex justify-center py-12">
        <PmsSpinner label="Chargement de la conversation" />
      </div>
    );
  }

  if (missing) {
    return (
      <p className="text-gray-700">
        Cette session n’existe pas ou a été effacée.{' '}
        <Link to="/admin/sessions" className="text-bleu-nuit underline underline-offset-2">
          Revenir aux sessions
        </Link>
        .
      </p>
    );
  }

  if (session.data === undefined) {
    return rejected ? null : (
      <p role="alert" className="text-rouge text-sm">
        Cette conversation n’a pas pu être lue. Rechargez la page.
      </p>
    );
  }

  const detail = session.data;
  const openLayers = readOpenLayers(search.couches);
  const arrivalTurn = readArrivalTurn(search.tour, detail.turns.length);

  return (
    <ConversationScreen
      detail={detail}
      guardrails={
        frozenRules.data?.content.guardrails ?? guardrails.data?.guardrails ?? NO_GUARDRAILS
      }
      rules={
        // The index of a rule of a version IS its priority (§8.5).
        frozenRules.data?.content.rules.map((rule, priority) => ({ ...rule, priority })) ??
        rules.data?.rules ??
        NO_RULES
      }
      budgetPerStepEur={
        frozenModels.data?.content.params.budgetPerStepEur ??
        models.data?.params.budgetPerStepEur ??
        null
      }
      openLayers={openLayers}
      onLayers={(next) =>
        void navigate({
          to: '/admin/sessions/$sessionId/protocole',
          params: { sessionId },
          search: (current) => ({ ...current, couches: writeOpenLayers(next) }),
        })
      }
      arrivalTurn={arrivalTurn}
      breadcrumb={
        <nav aria-label="Fil d’Ariane" className="flex flex-wrap gap-2 text-gray-600 text-sm">
          <Link to="/admin/sessions" className="text-bleu-nuit no-underline hover:underline">
            Sessions
          </Link>
          <span aria-hidden="true">›</span>
          <Link
            to="/admin/sessions/$sessionId"
            params={{ sessionId }}
            className="text-bleu-nuit no-underline hover:underline"
          >
            {detail.session.experienceName ?? 'Session'}
          </Link>
          <span aria-hidden="true">›</span>
          <span>Protocole</span>
        </nav>
      }
      // The SAME « Exporter en » menu as the parcours (§12.6, issue #173), and
      // « Retour au parcours » in its shape: one component, one style.
      actions={<ConversationActions session={detail.session} />}
    />
  );
}
